import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { movies } from '@/mocks/movies';
import { news } from '@/mocks/news';
import MovieCard from '@/components/base/MovieCard';
import SectionHeader from '@/components/base/SectionHeader';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import CategoryGrid from '@/pages/home/components/CategoryGrid';

export default function Home() {
  useDocumentTitle('蜜桃网-最新电影电视剧资讯与影视资源推荐', {
    description:
      '蜜桃网为您提供最新电影、电视剧资讯及影视资源推荐，覆盖多类型影视剧集，满足用户观影需求，打造全方位影视娱乐平台。',
    keywords: '蜜桃网,影视资讯,电影推荐,电视剧更新,影视资源,影视娱乐,电影电视剧',
  });

  const latestMovies = [...movies].sort((a, b) => b.year - a.year).slice(0, 8);
  const hotNews = news.slice(0, 5);
  const topRated = [...movies].sort((a, b) => b.rating - a.rating).slice(0, 6);

  return (
    <>
      <HeroCarousel />

      <section className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto w-full px-4 py-6 md:px-8 md:py-8">
          <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            蜜桃网 · 最新影视资讯与资源推荐平台
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            聚合最新电影、电视剧、综艺、动漫资讯与资源推荐，帮你快速找到想看的好内容。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full px-4 py-10 md:px-8 md:py-14" data-product-shop>
        <SectionHeader title="分类快捷入口" subtitle="按类型快速发现你想看的影视内容" />
        <CategoryGrid />
      </section>

      <section className="w-full bg-background-100" data-product-shop>
        <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
          <SectionHeader
            title="最新影视推荐"
            subtitle="最新上映与更新的影视资源，追剧不掉队"
            moreTo="/category/电影"
            moreLabel="浏览全部"
          />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {latestMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
          <div className="lg:col-span-2">
            <SectionHeader
              title="热门资讯"
              subtitle="最新影视新闻、影评与专题报道"
              moreTo="/news"
              moreLabel="进入资讯频道"
            />
            <div className="flex flex-col gap-4">
              {hotNews.map((article) => (
                <Link
                  key={article.id}
                  to={`/news/detail/${article.id}`}
                  className="group flex gap-4 overflow-hidden rounded-lg border border-background-200/70 bg-background-50 p-3 transition-all duration-300 hover:border-primary-300 hover:bg-background-100"
                >
                  <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md md:h-24 md:w-36">
                    <img
                      src={article.cover}
                      alt={article.title}
                      title={`${article.title} - 蜜桃网资讯`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="text-xs font-medium text-accent-600">{article.category}</span>
                    <h3 className="mt-1 line-clamp-2 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                      {article.title}
                    </h3>
                    <p className="mt-1 hidden line-clamp-2 text-xs leading-relaxed text-foreground-500 sm:block">
                      {article.excerpt}
                    </p>
                    <span className="mt-auto pt-2 text-xs text-foreground-400">
                      {article.author} · {article.date}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <SectionHeader title="高分榜单" subtitle="口碑佳作精选" />
            <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
              {topRated.map((movie, index) => (
                <Link
                  key={movie.id}
                  to={`/detail/${movie.id}`}
                  className="group flex items-center gap-3 border-b border-background-200/60 px-4 py-3 last:border-b-0 transition-colors hover:bg-background-100"
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                      index < 3
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-200 text-foreground-600'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <div className="h-14 w-10 shrink-0 overflow-hidden rounded-md">
                    <img
                      src={movie.poster}
                      alt={movie.title}
                      title={`${movie.title} - 蜜桃网影视推荐`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
                      {movie.title}
                    </h3>
                    <p className="truncate text-xs text-foreground-500">{movie.genres.join(' · ')}</p>
                  </div>
                  <span className="flex items-center gap-0.5 text-sm font-semibold text-accent-600">
                    <i className="ri-star-fill text-xs text-primary-400" />
                    {movie.rating}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100">
        <div className="mx-auto w-full px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 items-center gap-8 rounded-lg border border-background-200/70 bg-background-50 p-6 md:grid-cols-3 md:p-10">
            <div className="md:col-span-1">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 to-accent-500 text-background-50">
                <i className="ri-movie-2-line text-3xl" />
              </span>
            </div>
            <div className="md:col-span-2">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">关于蜜桃网</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                蜜桃网是一个面向影视爱好者与普通观众的影视资讯与资源推荐平台，聚合最新电影、电视剧、综艺、动漫等多类型影视内容。我们通过清晰的分类、专业的筛选与编辑精选，帮助你快速找到想看的好内容，打造优质的全方位影视娱乐平台。
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/news"
                  className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  浏览影视资讯
                </Link>
                <Link
                  to="/category/电影"
                  className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  探索影视资源
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}